/**
 * enterprise.js — LensPro Enterprise Feature Utilities
 * Covers: GPS, Watermark, Projects, Checklists, OCR, AI Categories,
 *         Review Workflow, Sync Queue, Report helpers.
 */
import { getAll, putRecord, deleteRecord, uid, getSetting, saveSetting } from "./indexedDb.js";
import { requestGroqAnalysis } from "./aiClient.js";

// ═══════════════════════════════════════════════════════════════════════════
//  1. WATERMARK
// ═══════════════════════════════════════════════════════════════════════════

export async function applyWatermark(blob, settings = {}) {
  const {
    text     = "LensPro",
    position = "br",        // tl | tr | bl | br
    opacity  = 0.82,
    fontSize = 14,
    color    = "#ffffff",
  } = settings;

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas        = document.createElement("canvas");
      canvas.width        = img.naturalWidth;
      canvas.height       = img.naturalHeight;
      const ctx           = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);

      const scale = Math.max(1, img.naturalWidth / 1080);
      const fs    = Math.round(fontSize * scale);
      ctx.font    = `600 ${fs}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
      ctx.globalAlpha     = opacity;

      const pad           = Math.round(22 * scale);
      const tw            = ctx.measureText(text).width;

      let x, y;
      if      (position === "tl") { x = pad;                     y = pad + fs;             }
      else if (position === "tr") { x = canvas.width - tw - pad; y = pad + fs;             }
      else if (position === "bl") { x = pad;                     y = canvas.height - pad;  }
      else                        { x = canvas.width - tw - pad; y = canvas.height - pad;  }

      ctx.shadowColor    = "rgba(0,0,0,0.75)";
      ctx.shadowBlur     = 7 * scale;
      ctx.shadowOffsetX  = 1 * scale;
      ctx.shadowOffsetY  = 1 * scale;
      ctx.fillStyle      = color;
      ctx.fillText(text, x, y);
      ctx.globalAlpha    = 1;
      ctx.shadowBlur     = 0;
      ctx.shadowOffsetX  = 0;
      ctx.shadowOffsetY  = 0;

      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("Watermark blob failed"))),
        "image/jpeg", 0.92
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Watermark img failed")); };
    img.src = url;
  });
}

export function buildWatermarkText(template = "{app}", extra = {}) {
  const now = new Date();
  const date = now.toLocaleDateString();
  const time = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return template
    .replace(/{app}/g,      "LensPro")
    .replace(/{date}/g,     date)
    .replace(/{time}/g,     time)
    .replace(/{datetime}/g, `${date} ${time}`)
    .replace(/{custom}/g,   extra.customText || "");
}

export async function getWatermarkSettings() {
  return getSetting("wmSettings", {
    enabled:    false,
    template:   "{app} · {datetime}",
    position:   "br",
    opacity:    0.82,
    fontSize:   14,
    color:      "#ffffff",
    customText: "",
  });
}
export async function saveWatermarkSettings(s) { await saveSetting("wmSettings", s); }

// ═══════════════════════════════════════════════════════════════════════════
//  3. PROJECTS
// ═══════════════════════════════════════════════════════════════════════════

export const PROJECT_PALETTE = [
  "#42f6d7","#f97316","#a855f7","#3b82f6",
  "#22c55e","#ef4444","#f59e0b","#ec4899",
  "#06b6d4","#8b5cf6","#84cc16","#14b8a6",
];

export async function getProjects() {
  try {
    const all = await getAll("projects");
    return all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  } catch { return []; }
}

export async function createProject({ name, color, description = "" }) {
  const proj = {
    id: uid("proj"),
    name: name.trim(),
    color: color || PROJECT_PALETTE[Math.floor(Math.random() * PROJECT_PALETTE.length)],
    description: description.trim(),
    photoCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  await putRecord("projects", proj);
  return proj;
}

export async function updateProject(proj) {
  const up = { ...proj, updatedAt: new Date().toISOString() };
  await putRecord("projects", up);
  return up;
}

export async function deleteProject(id) { await deleteRecord("projects", id); }

export async function getActiveProject() {
  const id = await getSetting("activeProjectId", null);
  if (!id) return null;
  return (await getProjects()).find((p) => p.id === id) || null;
}
export async function setActiveProject(id) { await saveSetting("activeProjectId", id); }

// ═══════════════════════════════════════════════════════════════════════════
//  4. CHECKLISTS
// ═══════════════════════════════════════════════════════════════════════════

export async function getChecklists() {
  try {
    const all = await getAll("checklists");
    return all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  } catch { return []; }
}

export async function createChecklist({ name, projectId = null, items = [] }) {
  const chk = {
    id: uid("chk"),
    name: name.trim(),
    projectId,
    items: items.map((raw, i) => ({
      id:       uid("ci"),
      label:    typeof raw === "string" ? raw.trim() : raw.label,
      captured: false,
      photoId:  null,
      order:    i,
    })),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  await putRecord("checklists", chk);
  return chk;
}

export async function updateChecklist(chk) {
  const up = { ...chk, updatedAt: new Date().toISOString() };
  await putRecord("checklists", up);
  return up;
}

export async function deleteChecklist(id) { await deleteRecord("checklists", id); }

export async function tickChecklistItem(checklistId, itemId, photoId = null) {
  const all = await getChecklists();
  const chk = all.find((c) => c.id === checklistId);
  if (!chk) return null;
  const items = chk.items.map((it) =>
    it.id === itemId
      ? { ...it, captured: true, photoId, capturedAt: new Date().toISOString() }
      : it
  );
  return updateChecklist({ ...chk, items });
}

export async function getActiveChecklist() {
  const id = await getSetting("activeChecklistId", null);
  if (!id) return null;
  return (await getChecklists()).find((c) => c.id === id) || null;
}
export async function setActiveChecklist(id) { await saveSetting("activeChecklistId", id); }

// ═══════════════════════════════════════════════════════════════════════════
//  5. AI CATEGORIZATION
// ═══════════════════════════════════════════════════════════════════════════

export const ENTERPRISE_CATEGORIES = [
  { id: "invoice",    label: "Invoice",    emoji: "🧾", color: "#f97316" },
  { id: "receipt",    label: "Receipt",    emoji: "🏷️",  color: "#f59e0b" },
  { id: "contract",   label: "Contract",   emoji: "📄", color: "#3b82f6" },
  { id: "id-card",    label: "ID / Card",  emoji: "🪪", color: "#8b5cf6" },
  { id: "person",     label: "Person",     emoji: "👤", color: "#22c55e" },
  { id: "landscape",  label: "Landscape",  emoji: "🌄", color: "#14b8a6" },
  { id: "product",    label: "Product",    emoji: "📦", color: "#f43f5e" },
  { id: "food",       label: "Food",       emoji: "🍽️",  color: "#84cc16" },
  { id: "document",   label: "Document",   emoji: "📋", color: "#64748b" },
  { id: "damage",     label: "Damage",     emoji: "⚠️",  color: "#ef4444" },
  { id: "whiteboard", label: "Whiteboard", emoji: "🖊️",  color: "#0ea5e9" },
  { id: "other",      label: "Other",      emoji: "📷", color: "#6b7280" },
];

export function getCategoryInfo(id) {
  return ENTERPRISE_CATEGORIES.find((c) => c.id === id) || ENTERPRISE_CATEGORIES.at(-1);
}

export async function categorizeImage(imageDataUrl) {
  const ids = ENTERPRISE_CATEGORIES.map((c) => c.id).join(", ");
  const result = await requestGroqAnalysis({
    action: "AI image categorization",
    prompt: `Classify this image into exactly ONE category from: ${ids}. Respond ONLY with valid JSON (no markdown): {"category":"<id>","confidence":<0-100>,"reason":"<brief reason>"}`,
    imageDataUrl,
    modelPreference: "vision",
  });
  try {
    const text  = result.result || result.text || "";
    const match = text.match(/\{[\s\S]*?"category"[\s\S]*?\}/);
    const json  = JSON.parse(match?.[0] || "{}");
    return {
      category:   json.category   || "other",
      confidence: typeof json.confidence === "number" ? json.confidence : 0,
      reason:     json.reason     || "",
    };
  } catch {
    return { category: "other", confidence: 0, reason: "" };
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//  6. OCR TEXT EXTRACTION
// ═══════════════════════════════════════════════════════════════════════════

export async function extractOCR(imageDataUrl) {
  const result = await requestGroqAnalysis({
    action: "OCR text extraction",
    prompt: `Extract ALL visible text exactly as it appears, then produce a structured summary. Return ONLY valid JSON (no markdown): {"rawText":"<all text verbatim>","structured":{"type":"<receipt|invoice|contract|form|id|whiteboard|other>","title":"<heading if any>","keyFields":[{"label":"<field>","value":"<value>"}],"summary":"<2 sentence overview>"}}`,
    imageDataUrl,
    modelPreference: "vision",
  });
  try {
    const text  = result.result || result.text || "";
    const match = text.match(/\{[\s\S]*?"rawText"[\s\S]*?\}/);
    const json  = JSON.parse(match?.[0] || "{}");
    return { rawText: json.rawText || text, structured: json.structured || null };
  } catch {
    return { rawText: result.result || result.text || "", structured: null };
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//  7. SYNC QUEUE
// ═══════════════════════════════════════════════════════════════════════════

export async function getSyncQueue() {
  try {
    const all = await getAll("syncQueue");
    return all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  } catch { return []; }
}

export async function addToSyncQueue({ photoId, photoName, endpoint = "" }) {
  const entry = {
    id:        uid("sq"),
    photoId,
    photoName: photoName || "Photo",
    endpoint,
    status:    "pending",   // pending | syncing | done | failed
    attempts:  0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  await putRecord("syncQueue", entry);
  return entry;
}

export async function updateSyncEntry(e) {
  return putRecord("syncQueue", { ...e, updatedAt: new Date().toISOString() });
}

export async function removeSyncEntry(id) { return deleteRecord("syncQueue", id); }

export async function clearDoneSyncEntries() {
  const done = (await getSyncQueue()).filter((e) => e.status === "done");
  await Promise.all(done.map((e) => removeSyncEntry(e.id)));
}

export async function processSyncQueue() {
  if (!navigator.onLine) return { synced: 0, failed: 0, total: 0 };
  const pending = (await getSyncQueue()).filter((e) => e.status === "pending");
  let synced = 0, failed = 0;
  for (const entry of pending) {
    await updateSyncEntry({ ...entry, status: "syncing" });
    if (!entry.endpoint) {
      await updateSyncEntry({ ...entry, status: "done" });
      synced++;
      continue;
    }
    try {
      const resp = await fetch(entry.endpoint, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ photoId: entry.photoId, appName: "LensPro", timestamp: new Date().toISOString() }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      await updateSyncEntry({ ...entry, status: "done", attempts: entry.attempts + 1 });
      synced++;
    } catch {
      await updateSyncEntry({ ...entry, status: "failed", attempts: entry.attempts + 1 });
      failed++;
    }
  }
  return { synced, failed, total: pending.length };
}

// ═══════════════════════════════════════════════════════════════════════════
//  9. REPORT PDF BUILDER (data helpers — rendering in ReportBuilder.jsx)
// ═══════════════════════════════════════════════════════════════════════════

export async function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload  = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

export async function generateReport({ title, subtitle, company, date, photos, notes = {} }) {
  const { jsPDF } = await import("jspdf");

  const PAGE_W = 595, PAGE_H = 842; // A4 in pt
  const MARGIN = 40;
  const CONTENT_W = PAGE_W - MARGIN * 2;

  const pdf = new jsPDF({ unit: "pt", format: "a4" });

  // ── Cover page ───────────────────────────────────────────────────────────
  // Dark background
  pdf.setFillColor(10, 14, 20);
  pdf.rect(0, 0, PAGE_W, PAGE_H, "F");

  // Accent bar
  pdf.setFillColor(66, 246, 215);
  pdf.rect(MARGIN, 120, 6, 80, "F");

  // Title
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(28);
  pdf.setFont("helvetica", "bold");
  pdf.text(title || "LensPro Report", MARGIN + 18, 165);

  if (subtitle) {
    pdf.setFontSize(14);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(160, 170, 180);
    pdf.text(subtitle, MARGIN + 18, 192);
  }

  // Meta row
  pdf.setFontSize(10);
  pdf.setTextColor(120, 130, 140);
  const meta = [company, date || new Date().toLocaleDateString()].filter(Boolean).join("  ·  ");
  pdf.text(meta, MARGIN, PAGE_H - MARGIN - 20);

  pdf.setFontSize(9);
  pdf.setTextColor(80, 90, 100);
  pdf.text(`${photos.length} photo${photos.length !== 1 ? "s" : ""}  ·  Generated by LensPro AI Camera`, MARGIN, PAGE_H - MARGIN);

  // ── Photo pages ──────────────────────────────────────────────────────────
  for (let i = 0; i < photos.length; i++) {
    pdf.addPage();
    const item  = photos[i];
    const blob  = item.editedBlob || item.blob;
    const note  = notes[item.id] || "";

    // Dark bg
    pdf.setFillColor(10, 14, 20);
    pdf.rect(0, 0, PAGE_W, PAGE_H, "F");

    // Page number
    pdf.setFontSize(9);
    pdf.setTextColor(80, 90, 100);
    pdf.text(`${i + 1} / ${photos.length}`, PAGE_W - MARGIN, MARGIN - 10, { align: "right" });

    if (blob) {
      const dataUrl = await blobToDataUrl(blob);
      const imgObj  = await new Promise((res) => {
        const im = new Image();
        im.onload = () => res(im);
        im.src    = dataUrl;
      });
      const aspect  = imgObj.naturalHeight / imgObj.naturalWidth;
      const imgW    = CONTENT_W;
      const imgH    = Math.min(imgW * aspect, 420);
      const imgY    = MARGIN;
      pdf.addImage(dataUrl, "JPEG", MARGIN, imgY, imgW, imgH, undefined, "FAST");

      let curY = imgY + imgH + 18;

      // Photo name
      pdf.setFontSize(13);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(230, 235, 240);
      pdf.text(item.name || item.title || `Photo ${i + 1}`, MARGIN, curY);
      curY += 18;

      // Meta chips row
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(140, 150, 160);
      const metaParts = [];
      if (item.createdAt) metaParts.push(new Date(item.createdAt).toLocaleString());
      if (item.projectName) metaParts.push(`Project: ${item.projectName}`);
      if (item.aiCategory && item.aiCategory !== "other") metaParts.push(`Category: ${item.aiCategory}`);
      if (item.reviewStatus && item.reviewStatus !== "none") metaParts.push(`Status: ${item.reviewStatus}`);
      pdf.text(metaParts.join("  ·  "), MARGIN, curY);
      curY += 16;

      // Note
      if (note) {
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(180, 185, 195);
        const lines = pdf.splitTextToSize(note, CONTENT_W);
        pdf.text(lines, MARGIN, curY);
      }
    }
  }

  return pdf;
}
